import React, { useEffect, useState } from 'react';
import { Grid, Typography, styled, useTheme, Button, Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../redux/hooks';
import {
  IActivitySummary,
  clearActivityProgressSummary,
  getClientActivityProgressSummaryRequest,
  setProgressSingleActivityBlock,
} from '../../../redux/client/clientSlice';
import { ActivityTypeEnum, AdvanceActivityStepType } from '../../../types/activity';
import { capitalizeWords } from '../../ClientDetail/Legend';
import StyledTextButton from '../../Common/StyledTextButton';
import { renderActivitySchedule } from '../Progress';
import AdavanceActivityCharts from '../AdavanceActivityCharts';
import ActivitiesLoading from './ActivitiesLoading';
import Voice from '../AnswersTable/Voice';
import { StyledTypography } from '../../Common/StyledSpan';
import { formatDateTime } from '../../../utils/dateUtils';

const ShowMoreDetailsButton = styled(Button)(() => ({
  padding: '0px !important',
  '& span': {
    background: 'linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontFamily: 'Satoshi',
    fontSize: '17px',
    fontWeight: 700,
    lineHeight: '22px',
  },
}));

export function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function Activities() {
  const dispatch = useDispatch();
  const { id: clientId } = useParams<{ id: string }>();
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const activityProgress = useAppSelector(
    (state) => state.client.activityProgressSummary.activitySummary
  );
  const activityCount = useAppSelector((state) => state.client.activityProgressSummary.count);
  const theme = useTheme();
  const [skip, setSkip] = useState(2);
  const { t } = useTranslation();
  const [showMoreDetails, setShowMoreDetails] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    dispatch(
      getClientActivityProgressSummaryRequest({
        clientId: clientId!,
        coachId,
        skip: 0,
        take: 3,
      })
    );
  }, [clientId, coachId]);

  const hasMore = () => activityProgress.length < activityCount;
  const fetchMoreData = () => {
    if (!coachId || !clientId) {
      return;
    }
    const newSkip = skip + 1;
    setSkip(newSkip);
    dispatch(
      getClientActivityProgressSummaryRequest({
        clientId,
        coachId,
        skip: newSkip,
        take: 1,
      })
    );
  };

  const toggleShowDetails = (keyToToggle: string) => {
    setShowMoreDetails((prevState) => ({
      ...prevState,
      [keyToToggle]: !prevState[keyToToggle],
    }));
  };

  const handleShowMoreDetails = (activity: IActivitySummary) => {
    dispatch(setProgressSingleActivityBlock(activity));
  };

  useEffect(
    () => () => {
      dispatch(clearActivityProgressSummary());
    },
    []
  );

  return (
    <InfiniteScroll
      scrollableTarget="single-activity-progress"
      dataLength={activityProgress.length}
      next={fetchMoreData}
      hasMore={hasMore()}
      loader={<ActivitiesLoading />}
    >
      <Grid display="flex" flexDirection="column" gap={6} overflow="hidden">
        {activityProgress?.map((activity) => (
          <Grid key={activity.groupId} gap={2} display="flex" flexDirection="column">
            <Grid display="flex" justifyContent="space-between">
              <Grid gap="12px" display="flex" flexDirection="row">
                <Typography variant="h6" color="primary">
                  {capitalizeWords(activity.name)}
                </Typography>
                {renderActivitySchedule(activity.schedule as ActivityTypeEnum)}
              </Grid>
              {activity.schedule !== ActivityTypeEnum.ONETIME && (
                <Grid>
                  <StyledTextButton
                    onClick={() => handleShowMoreDetails(activity)}
                    variant="text"
                    endIcon={<img src="/assets/icons/arrow-right.svg" alt="arrow-right" />}
                  >
                    <span>{t('clientProgress.showMoreDetails')}</span>
                  </StyledTextButton>
                </Grid>
              )}
            </Grid>
            <Grid
              display="flex"
              container
              bgcolor="#fff"
              padding={theme.spacing(3)}
              boxShadow={theme.borderStyles.cardBorders}
              borderRadius="10px"
              rowGap="36px"
            >
              <Grid
                item
                flexDirection="column"
                height="100%"
                flex={{ xs: 12, sm: 12, md: 6, lg: 6 }}
              >
                <StyledTypography fontSize={11} fontWeight={700} color="primary">
                  <span>{t('common.date')}</span>
                </StyledTypography>
                <Typography fontWeight={500}>
                  {formatDateTime(activity?.activityDetails[0]?.completedAt).formattedDay}
                </Typography>
              </Grid>
              <Grid item flex={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
                <StyledTypography fontSize={11} fontWeight={700} color="primary">
                  <span>{t('common.completedTime')}</span>
                </StyledTypography>
                <Typography fontWeight={500}>
                  {formatDateTime(activity?.activityDetails[0]?.completedAt).formattedTime}
                </Typography>
              </Grid>
            </Grid>
            <AdavanceActivityCharts activity={activity} />
            {activity.schedule === ActivityTypeEnum.ONETIME && activity.responseSummary.length > 0 && (
              <Grid
                container
                bgcolor="#fff"
                padding={theme.spacing(3)}
                boxShadow={theme.borderStyles.cardBorders}
                borderRadius="10px"
                rowGap="36px"
              >
                {showMoreDetails[activity.groupId] &&
                  activity.responseSummary.map((oneTimeActivity) => {
                    if (oneTimeActivity.advanceStepType === AdvanceActivityStepType.PERCENTAGE) {
                      return (
                        <Grid
                          key={oneTimeActivity.text}
                          item
                          xs={6}
                          gap="4px"
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography variant="bodyBold" color="primary">
                            {capitalizeFirstLetter(oneTimeActivity.text)}
                          </Typography>
                          <Typography variant="bodyRegular" color="primary">
                            {`${Math.round(Number(oneTimeActivity.data[0].value))}%`}
                          </Typography>
                        </Grid>
                      );
                    }
                    if (
                      oneTimeActivity.advanceStepType === AdvanceActivityStepType.ANY &&
                      oneTimeActivity.data[0].type === 'IMAGE'
                    ) {
                      return (
                        <Grid
                          key={oneTimeActivity.text}
                          item
                          xs={6}
                          gap="4px"
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography variant="bodyBold" color="primary">
                            {capitalizeFirstLetter(oneTimeActivity.text)}
                          </Typography>
                          <Box>
                            <img
                              src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${oneTimeActivity.data[0].value}`}
                              alt="activity response"
                              style={{ width: '300px', height: '262px', borderRadius: '10px' }}
                            />
                          </Box>
                        </Grid>
                      );
                    }
                    if (
                      oneTimeActivity.advanceStepType === AdvanceActivityStepType.ANY &&
                      oneTimeActivity.data[0].type === 'VIDEO'
                    ) {
                      return (
                        <Grid
                          key={oneTimeActivity.text}
                          item
                          xs={6}
                          gap="4px"
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography variant="bodyBold" color="primary">
                            {capitalizeFirstLetter(oneTimeActivity.text)}
                          </Typography>
                          <Box>
                            <video
                              src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${oneTimeActivity.data[0].value}`}
                              controls
                              style={{ width: '300px', height: '262px' }}
                            >
                              <track kind="captions" src="captions.vtt" label="English" />
                            </video>
                          </Box>
                        </Grid>
                      );
                    }
                    if (
                      oneTimeActivity.advanceStepType === AdvanceActivityStepType.ANY &&
                      oneTimeActivity.data[0].type === 'AUDIO'
                    ) {
                      return (
                        <Grid
                          key={oneTimeActivity.text}
                          item
                          xs={6}
                          gap="4px"
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography variant="bodyBold" color="primary">
                            {capitalizeFirstLetter(oneTimeActivity.text)}
                          </Typography>
                          <Box>
                            <Voice message={oneTimeActivity.data[0].value} />
                          </Box>
                        </Grid>
                      );
                    }
                    if (
                      oneTimeActivity.advanceStepType === AdvanceActivityStepType.ANY &&
                      oneTimeActivity.data[0].type === 'DOCUMENT'
                    ) {
                      return (
                        <Grid
                          key={oneTimeActivity.text}
                          item
                          xs={6}
                          gap="4px"
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography variant="bodyBold" color="primary">
                            {capitalizeFirstLetter(oneTimeActivity.text)}
                          </Typography>
                          <Box>
                            <iframe
                              title={oneTimeActivity.data[0].value}
                              src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${oneTimeActivity.data[0].value}`}
                              style={{ width: '300px', height: '262px' }}
                            >
                              This browser does not support PDFs. Please download the PDF to view
                              it.
                            </iframe>
                          </Box>
                        </Grid>
                      );
                    }
                    return (
                      <Grid
                        key={oneTimeActivity.text}
                        item
                        xs={6}
                        gap="4px"
                        display="flex"
                        flexDirection="column"
                      >
                        <Typography variant="bodyBold" color="primary">
                          {capitalizeFirstLetter(oneTimeActivity.text)}
                        </Typography>
                        <Typography variant="bodyRegular" color="primary">
                          {oneTimeActivity.data[0].value}
                        </Typography>
                      </Grid>
                    );
                  })}
                {!showMoreDetails[activity.groupId] &&
                  activity.responseSummary.slice(0, 2).map((oneTimeActivity) => {
                    if (oneTimeActivity.advanceStepType === AdvanceActivityStepType.PERCENTAGE) {
                      return (
                        <Grid
                          key={oneTimeActivity.text}
                          item
                          xs={6}
                          gap="4px"
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography variant="bodyBold" color="primary">
                            {capitalizeFirstLetter(oneTimeActivity.text)}
                          </Typography>
                          <Typography variant="bodyRegular" color="primary">
                            {`${Math.round(Number(oneTimeActivity.data[0].value))}%`}
                          </Typography>
                        </Grid>
                      );
                    }
                    if (
                      oneTimeActivity.advanceStepType === AdvanceActivityStepType.ANY &&
                      oneTimeActivity.data[0].type === 'IMAGE'
                    ) {
                      return (
                        <Grid
                          key={oneTimeActivity.text}
                          item
                          xs={6}
                          gap="4px"
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography variant="bodyBold" color="primary">
                            {capitalizeFirstLetter(oneTimeActivity.text)}
                          </Typography>
                          <Box>
                            <img
                              src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${oneTimeActivity.data[0].value}`}
                              alt="activity response"
                              style={{ width: '300px', height: '262px', borderRadius: '10px' }}
                            />
                          </Box>
                        </Grid>
                      );
                    }
                    if (
                      oneTimeActivity.advanceStepType === AdvanceActivityStepType.ANY &&
                      oneTimeActivity.data[0].type === 'VIDEO'
                    ) {
                      return (
                        <Grid
                          key={oneTimeActivity.text}
                          item
                          xs={6}
                          gap="4px"
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography variant="bodyBold" color="primary">
                            {capitalizeFirstLetter(oneTimeActivity.text)}
                          </Typography>
                          <Box>
                            <video
                              src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${oneTimeActivity.data[0].value}`}
                              controls
                              style={{ width: '300px', height: '262px' }}
                            >
                              <track kind="captions" src="captions.vtt" label="English" />
                            </video>
                          </Box>
                        </Grid>
                      );
                    }
                    if (
                      oneTimeActivity.advanceStepType === AdvanceActivityStepType.ANY &&
                      oneTimeActivity.data[0].type === 'AUDIO'
                    ) {
                      return (
                        <Grid
                          key={oneTimeActivity.text}
                          item
                          xs={6}
                          gap="4px"
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography variant="bodyBold" color="primary">
                            {capitalizeFirstLetter(oneTimeActivity.text)}
                          </Typography>
                          <Box>
                            <Voice message={oneTimeActivity.data[0].value} />
                          </Box>
                        </Grid>
                      );
                    }
                    if (
                      oneTimeActivity.advanceStepType === AdvanceActivityStepType.ANY &&
                      oneTimeActivity.data[0].type === 'DOCUMENT'
                    ) {
                      return (
                        <Grid
                          key={oneTimeActivity.text}
                          item
                          xs={6}
                          gap="4px"
                          display="flex"
                          flexDirection="column"
                        >
                          <Typography variant="bodyBold" color="primary">
                            {capitalizeFirstLetter(oneTimeActivity.text)}
                          </Typography>
                          <Box>
                            <iframe
                              title={oneTimeActivity.data[0].value}
                              src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${oneTimeActivity.data[0].value}`}
                              style={{ width: '300px', height: '262px' }}
                            >
                              This browser does not support PDFs. Please download the PDF to view
                              it.
                            </iframe>
                          </Box>
                        </Grid>
                      );
                    }
                    return (
                      <Grid
                        key={oneTimeActivity.text}
                        item
                        xs={6}
                        gap="4px"
                        display="flex"
                        flexDirection="column"
                      >
                        <Typography variant="bodyBold" color="primary">
                          {capitalizeFirstLetter(oneTimeActivity.text)}
                        </Typography>
                        <Typography variant="bodyRegular" color="primary">
                          {oneTimeActivity.data[0].value}
                        </Typography>
                      </Grid>
                    );
                  })}
                {activity.responseSummary.length > 2 && (
                  <Grid item xs={12}>
                    <ShowMoreDetailsButton
                      onClick={() => {
                        toggleShowDetails(activity.groupId);
                      }}
                      variant="text"
                    >
                      <span>
                        {showMoreDetails[activity.groupId]
                          ? t('clientProgress.showLessDetails')
                          : t('clientProgress.showMoreDetails')}
                      </span>
                    </ShowMoreDetailsButton>
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
        ))}
      </Grid>
    </InfiniteScroll>
  );
}

export default Activities;
